// vault.js – with temporary faded upload indicator and local update to attachments
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import VaultDesktop from './vault_desktop';
import VaultMobile from './vault_mobile';
import { uploadVaultAttachment } from './vault_file_api';
import { sanitizeVaultHtml } from './vault_rich_editor';

const Vault = () => {
  const [documents, setDocuments] = useState(null);
  const [pendingUploads, setPendingUploads] = useState(new Set());
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [folders, setFolders] = useState([]);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('walmates-token')}` });

  const fetchFolders = () => {
    const user_id = localStorage.getItem('walmates-user_id');
    return axios.get(`/api/vault/folders?user_id=${user_id}`, { headers: authHeaders() })
      .then((result) => { setFolders(result.data || []); return result.data || []; });
  };

  const fetchVaultData = (dataOrKeyword) => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    let url = `/api/vault/get?user_id=${user_id}`;

    if (typeof dataOrKeyword === 'string' && dataOrKeyword.length > 0) {
      url += `&keyword=${encodeURIComponent(dataOrKeyword)}`;
    }

    return axios
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((result) => {
        const data = new Map();
        result.data.forEach((d) => {
          if (!data.has(d.vault_id)) {
            d.files = [];
            data.set(d.vault_id, d);
          }
          if (d.file_id && d.file_name) {
            const item = data.get(d.vault_id);
            item.files.push({
              id: d.file_id,
              vault_id: d.vault_id,
              user_id: d.file_user_id,
              file_name: d.file_name,
              extn: d.file_name.split('.').pop()?.toLowerCase(),
              name: d.file_name.replace(/\.[^/.]+$/, ''),
            });
          }
        });
        setDocuments(data);
        return data;
      });
  };

  /**
   * Create a vault note. Requires a non-empty title.
   * Optional rich-text HTML body is saved immediately after create.
   * Returns a Promise that resolves to the new vault_id.
   */
  const handleDocumentAdd = async (title, body = '', folderName = '') => {
    const trimmed = String(title || '').trim();
    if (!trimmed) {
      throw new Error('Title is required');
    }

    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    const message = `user_id=${user_id}&title=${encodeURIComponent(trimmed)}`;

    try {
      const res = await axios.get(`/api/vault/add?${message}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const vaultId = res?.data?.vault_id;
      const dataHtml = sanitizeVaultHtml(body);
      if (vaultId != null && dataHtml) {
        const updateMsg = `user_id=${user_id}&vault_id=${vaultId}&data=${encodeURIComponent(dataHtml)}`;
        await axios.get(`/api/vault/update?${updateMsg}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      if (vaultId != null && folderName) {
        const folderTag = `__folder_${encodeURIComponent(folderName)}`;
        const updateMsg = `user_id=${user_id}&vault_id=${vaultId}&tags=${encodeURIComponent(folderTag)}`;
        await axios.get(`/api/vault/update?${updateMsg}`, { headers: authHeaders() });
      }
      await fetchVaultData('');
      await fetchFolders();
      if (vaultId != null) setSelectedNoteId(vaultId);
      return vaultId;
    } catch (err) {
      console.error(`${trimmed}: Failed to add`, err);
      throw err;
    }
  };

  const handleUpdate = (id, field, text) => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    const message = `user_id=${user_id}&vault_id=${id}&${field}=${encodeURIComponent(text)}`;

    // Optimistic local update so sidebar title / preview stay in sync while autosaving.
    setDocuments((prev) => {
      if (!prev) return prev;
      const key = prev.has(id)
        ? id
        : prev.has(String(id))
          ? String(id)
          : prev.has(Number(id))
            ? Number(id)
            : null;
      if (key == null) return prev;
      const updated = new Map(prev);
      const doc = { ...updated.get(key), [field]: text };
      updated.set(key, doc);
      return updated;
    });

    axios
      .get(`/api/vault/update?${message}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .catch((err) => {
        console.error(`Failed to update vault_id:${id}`, err);
      });
  };

  const onFileUpload = (vault_id, selectedFile) => {
    const user_id = localStorage.getItem('walmates-user_id');

    const tempId = selectedFile.name;
    const placeholder = {
      id: tempId,
      vault_id,
      user_id,
      file_name: selectedFile.name,
      extn: selectedFile.name.split('.').pop()?.toLowerCase(),
      name: selectedFile.name.replace(/\.[^/.]+$/, ''),
      pending: true,
    };

    setDocuments((prev) => {
      const updated = new Map(prev);
      const doc = { ...updated.get(vault_id) };
      doc.files = [...(doc.files || []), placeholder];
      updated.set(vault_id, doc);
      return updated;
    });

    setPendingUploads((prev) => new Set(prev).add(selectedFile.name));

    uploadVaultAttachment(vault_id, selectedFile)
      .then((confirmed) => {
        console.log('File uploaded to R2');
        const file_id = confirmed?.file_id;
        if (!file_id) return;

        const newFile = {
          id: file_id,
          pending: false,
          vault_id,
          user_id,
          file_name: selectedFile.name,
          extn: selectedFile.name.split('.').pop()?.toLowerCase(),
          name: selectedFile.name.replace(/\.[^/.]+$/, ''),
        };

        setDocuments((prev) => {
          const updated = new Map(prev);
          const doc = { ...updated.get(vault_id) };
          doc.files = [
            ...doc.files.filter(
              (f) => !(f.file_name === selectedFile.name && f.pending)
            ),
            newFile,
          ];
          updated.set(vault_id, doc);
          return updated;
        });
      })
      .catch((error) => {
        console.error(error);
        setDocuments((prev) => {
          const updated = new Map(prev);
          const doc = { ...updated.get(vault_id) };
          if (doc) {
            doc.files = (doc.files || []).filter(
              (f) => !(f.file_name === selectedFile.name && f.pending)
            );
            updated.set(vault_id, doc);
          }
          return updated;
        });
      })
      .finally(() => {
        setPendingUploads((prev) => {
          const updated = new Set(prev);
          updated.delete(selectedFile.name);
          return updated;
        });
      });
  };

  const onFileRemove = (vault_id, file_id) => {
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    axios
      .get(
        `/api/vault/removefile?user_id=${user_id}&vault_id=${vault_id}&file_id=${file_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(() => {
        console.log('File removed');
        setDocuments((prev) => {
          const updated = new Map(prev);
          const doc = { ...updated.get(vault_id) };
          doc.files = doc.files.filter((f) => f.id !== file_id);
          updated.set(vault_id, doc);
          return updated;
        });
      })
      .catch((error) => console.error(error));
  };

  const handleShareSaved = (vaultId, settings) => {
    setDocuments((prev) => {
      if (!prev) return prev;
      const key = prev.has(vaultId) ? vaultId : prev.has(String(vaultId)) ? String(vaultId) : Number(vaultId);
      if (!prev.has(key)) return prev;
      const updated = new Map(prev);
      updated.set(key, {
        ...updated.get(key),
        shared_with_all: !!settings.shared_with_all,
        shared_user_ids: settings.user_ids || [],
      });
      return updated;
    });
  };

  const handleCreateFolder = async (name) => {
    const user_id = localStorage.getItem('walmates-user_id');
    const result = await axios.post('/api/vault/folders', { user_id, name }, {
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    });
    await fetchFolders();
    return result.data;
  };

  const handleFolderShareSaved = (folderId, settings) => {
    setFolders((current) => current.map((folder) => String(folder.id) === String(folderId)
      ? { ...folder, shared_with_all: !!settings.shared_with_all, user_ids: settings.user_ids || [] }
      : folder));
  };

  useEffect(() => {
    fetchVaultData('');
    fetchFolders().catch((err) => console.error('Failed to load Vault folders', err));
  }, []);

  const isMobile = window.innerWidth < 768;

  return isMobile ? (
    <VaultMobile
      documents={documents}
      onSearchResults={fetchVaultData}
      onAddDocument={handleDocumentAdd}
      onUpdateNote={handleUpdate}
      onFileUpload={onFileUpload}
      onFileRemove={onFileRemove}
      pendingUploads={pendingUploads}
      selectedNoteId={selectedNoteId}
      setSelectedNoteId={setSelectedNoteId}
      onShareSaved={handleShareSaved}
      folders={folders}
      onCreateFolder={handleCreateFolder}
      onFolderShareSaved={handleFolderShareSaved}
    />
  ) : (
    <VaultDesktop
      documents={documents}
      onSearchResults={fetchVaultData}
      onAddDocument={handleDocumentAdd}
      onUpdateNote={handleUpdate}
      onFileUpload={onFileUpload}
      onFileRemove={onFileRemove}
      pendingUploads={pendingUploads}
      selectedNoteId={selectedNoteId}
      setSelectedNoteId={setSelectedNoteId}
      onShareSaved={handleShareSaved}
      folders={folders}
      onCreateFolder={handleCreateFolder}
      onFolderShareSaved={handleFolderShareSaved}
    />
  );
};

export default Vault;
